<script setup lang="ts">
import { ref, watch } from 'vue'
import axios from 'axios'
import { Clipboard, KeyRound, Loader2 } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/toast'
import { quenticTokenRotationPath, resolveQuenticCustomerId } from '@/utils/quenticToken'

const props = defineProps<{
  open: boolean
  customer: Record<string, any> | null
}>()

const emit = defineEmits<{
  (event: 'update:open', value: boolean): void
  (event: 'rotated', value: { customerId: string; generatedAt: string | null }): void
}>()

const { t } = useI18n()
const { toast } = useToast()
const token = ref('')
const loading = ref(false)
const errorMessage = ref('')

watch(() => props.open, (open) => {
  if (!open) return
  token.value = ''
  loading.value = false
  errorMessage.value = ''
})

function customerId() {
  return resolveQuenticCustomerId(props.customer)
}

function customerName() {
  return props.customer?.customerName || props.customer?.Kunde || customerId()
}

function hasToken() {
  return Boolean(props.customer?.hasQuenticToken ?? props.customer?.has_quentic_token)
}

async function rotateToken() {
  const id = customerId()
  if (id === null || id === undefined) return
  if (hasToken() && !window.confirm(t('view.quentic_token_rotate_confirm'))) return

  loading.value = true
  errorMessage.value = ''
  try {
    const response = await axios.post(quenticTokenRotationPath(props.customer)!)
    token.value = response.data.quentic_token || ''
    emit('rotated', {
      customerId: String(id),
      generatedAt: response.data.quentic_token_generated_at || null,
    })
  } catch (error: any) {
    errorMessage.value =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.response?.statusText ||
      t('view.quentic_token_failed')
  } finally {
    loading.value = false
  }
}

async function copyToken() {
  if (!token.value) return
  try {
    await navigator.clipboard.writeText(token.value)
    toast({ title: t('view.quentic_token_copied') })
  } catch (_error) {
    toast({ title: t('view.quentic_token_copy_failed'), variant: 'destructive' })
  }
}
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-xl">
      <DialogHeader>
        <DialogTitle>{{ t('view.quentic_token_title') }}</DialogTitle>
        <DialogDescription>
          {{ hasToken() ? t('view.quentic_token_rotate_description') : t('view.quentic_token_create_description') }}
        </DialogDescription>
      </DialogHeader>

      <div class="space-y-4">
        <p class="text-sm text-gray-700">{{ customerName() }}</p>
        <div v-if="token" class="space-y-2 rounded border border-amber-200 bg-amber-50 p-3">
          <Label for="quentic_token">{{ t('view.quentic_token_one_time') }}</Label>
          <div class="flex gap-2">
            <Input id="quentic_token" :model-value="token" readonly class="font-mono" />
            <Button type="button" variant="outline" size="icon" :title="t('view.quentic_token_copy')" @click="copyToken">
              <Clipboard class="h-4 w-4" />
            </Button>
          </div>
          <p class="text-xs text-amber-800">{{ t('view.quentic_token_store_warning') }}</p>
        </div>
        <p v-if="errorMessage" class="text-sm text-red-600">{{ errorMessage }}</p>
      </div>

      <DialogFooter>
        <Button variant="outline" :disabled="loading" @click="emit('update:open', false)">
          {{ t('view.close') }}
        </Button>
        <Button v-if="!token" class="greenbutton text-white" :disabled="loading" @click="rotateToken">
          <Loader2 v-if="loading" class="mr-2 h-4 w-4 animate-spin" />
          <KeyRound v-else class="mr-2 h-4 w-4" />
          {{ hasToken() ? t('view.quentic_token_rotate') : t('view.quentic_token_create') }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
